import { useMemo } from 'react';
import { FormComboboxMultiple } from 'src/components/form-combobox';
import FormSelect from 'src/components/form-select';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { convertSubnetMaskToLength } from 'src/utils/ip';

import { EUntaggedVlan, IEdgeExtraData, EAllowIncomingPackets } from '../types';

const untaggedVlanOptions = [
  { label: 'Let Network Studio choose', value: EUntaggedVlan.AUTO },
  { label: 'None', value: EUntaggedVlan.NONE },
];

const allowIncomingPacketsOptions = [
  { label: 'Let Network Studio choose', value: EAllowIncomingPackets.AUTO },
  { label: 'Allow tagged', value: EAllowIncomingPackets.ALLOW_TAGGED },
  { label: 'Allow untagged', value: EAllowIncomingPackets.ALLOW_UNTAGGED },
  {
    label: 'Allow tagged & untagged',
    value: EAllowIncomingPackets.ALLOW_TAGGED_AND_UNTAGGED,
  },
];

export function CustomEdgeEditVlan({
  data,
  onChangeEdgeData,
}: {
  data: IEdgeExtraData;
  onChangeEdgeData: (data: Partial<IEdgeExtraData>) => void;
}) {
  const { vlans, useCustomVlan } = useGlobalContext();
  const hasVlanIds = useMemo(() => vlans?.length > 0, [vlans]);

  const {
    vlanIds = [],
    untaggedVlan = EUntaggedVlan.AUTO,
    allowIncomingPackets = EAllowIncomingPackets.AUTO,
  } = data || {};

  return (
    <div
      className="mt-2 flex max-w-lg flex-col items-center justify-center gap-6 pt-2"
      data-testid="dialog-custom-edge-edit-vlan-panel"
    >
      {hasVlanIds && useCustomVlan && (
        <>
          <FormComboboxMultiple
            label="VLAN membership"
            description="Assign this connection to one or more VLANs."
            anchor="bottom start"
            dataTestId="dialog-custom-edge-edit-vlan-membership"
            className="w-full"
            optionWrapperClassName="w-84"
            options={vlans?.map((vlan) => ({
              label: vlan.vlanId.toString(),
              value: vlan.vlanId.toString(),
              color: vlan.color,
              description: vlan.description,
              caption: vlan.ipSubnet
                ? `${vlan.ipSubnet}/${convertSubnetMaskToLength(vlan.subnetMask || '')}`
                : 'N/A',
            }))}
            values={vlanIds.map((vlanId) => vlanId.toString())}
            onChange={(values) => {
              if (untaggedVlan && !values.includes(untaggedVlan.toString())) {
                onChangeEdgeData({
                  vlanIds: values.map((value) => parseInt(value, 10)),
                  untaggedVlan: EUntaggedVlan.AUTO,
                });
              } else {
                onChangeEdgeData({
                  vlanIds: values.map((value) => parseInt(value, 10)),
                });
              }
            }}
          />

          <FormSelect
            label="Untagged VLAN"
            data-testid="dialog-custom-edge-edit-vlan-untagged-vlan"
            description={
              <>
                <p>
                  Choose the VLAN that incoming packets without a VLAN Tag
                  belong to.
                </p>
                <p>
                  Outgoing packets of this VLAN will be sent without a VLAN Tag.
                </p>
              </>
            }
            className="w-full"
            options={[
              ...untaggedVlanOptions,
              ...vlanIds.map((vlanId) => ({
                label: `${vlanId} - ${vlans?.find((vlan) => vlan.vlanId === vlanId)?.description}`,
                value: vlanId.toString(),
              })),
            ]}
            value={untaggedVlan}
            onChange={(e) => {
              onChangeEdgeData({
                untaggedVlan: e.target.value as EUntaggedVlan,
              });
            }}
          />

          <FormSelect
            label="Allow incoming packets"
            data-testid="dialog-custom-edge-edit-vlan-allow-incoming-packets"
            className="w-full"
            options={allowIncomingPacketsOptions}
            value={allowIncomingPackets}
            onChange={(e) => {
              onChangeEdgeData({
                allowIncomingPackets: e.target.value as EAllowIncomingPackets,
              });
            }}
          />
        </>
      )}
    </div>
  );
}
